<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from './CollapsibleCard.vue'
import type { SignalData } from '../../api/client'
import { fxStrikeLabel } from '../../composables/useSignalPipeline'

const props = defineProps<{
  signal: SignalData | null
  nas100Return: number | null
  nas100MarketReturn: number | null
  gbpusdReturn: number | null
  gbpusdMarketReturn: number | null
  gbpusdRefDegraded: boolean
  fxWindPill: { label: string; cls: string; title: string } | null
}>()

const nas100Degraded = computed(() => !!props.signal?.nq_t2_degraded)
// Fund's NAV-strike hour for the FX pre-strike badge (BGA 10:00, JH 12:00).
const strike = computed(() => fxStrikeLabel(props.signal?.fund))
const alert = computed(() => nas100Degraded.value || props.gbpusdRefDegraded)

const summary = computed(() => {
  const n = props.nas100Return != null ? props.nas100Return * 100 : null
  const g = props.gbpusdReturn != null ? props.gbpusdReturn * 100 : null
  const nStr = n != null ? (n >= 0 ? '+' : '') + n.toFixed(2) + '%' : '--'
  const gStr = g != null ? (g >= 0 ? '+' : '') + g.toFixed(2) + '%' : '--'
  return `NAS100 ${nStr} · GBPUSD ${gStr}`
})
</script>

<template>
  <CollapsibleCard title="Market Context" testid="market-context-card" :summary="summary" :alert="alert">
    <!-- Context pills -->
    <div class="context-pills">
      <span class="ctx-pill">
        NAS100
        <span v-if="signal?.nas100_price" class="pill-value">{{ signal.nas100_price.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') }}</span>
        <div class="pill-returns">
          <span :class="(nas100Return ?? 0) >= 0 ? 'pill-positive' : 'pill-negative'">
            {{ (nas100Return ?? 0) >= 0 ? '▲' : '▼' }} {{ ((nas100Return ?? 0) * 100).toFixed(2) }}%
            <span class="return-label" :class="{ 'return-label-warn': nas100Degraded }" :title="nas100Degraded ? 'T-2 close unavailable — using T-1 (same as vs close)' : ''">signal{{ nas100Degraded ? ' ⚠' : '' }}</span>
          </span>
          <span v-if="nas100MarketReturn != null && !nas100Degraded" :class="nas100MarketReturn >= 0 ? 'pill-positive' : 'pill-negative'">
            {{ nas100MarketReturn >= 0 ? '▲' : '▼' }} {{ (nas100MarketReturn * 100).toFixed(2) }}%
            <span class="return-label">vs close</span>
          </span>
          <span v-if="nas100Degraded" class="pill-degraded" title="Signal and vs close are identical — T-2 close missing, using T-1 fallback">
            = vs close (T-1 fallback)
          </span>
        </div>
      </span>
      <span class="ctx-pill">
        GBPUSD
        <span v-if="signal?.gbpusd_rate" class="pill-value">{{ signal.gbpusd_rate.toFixed(4) }}</span>
        <div class="pill-returns">
          <!-- Pre-strike state: today's strike-time UK FX rate (BGA 10:00,
               JH 12:00) not yet captured, so gbpusd_ref falls back to
               yesterday's fix and is identical to gbpusd_prev_close_t1
               (the "vs close" baseline). Hide the vs-close pill and surface
               the collapse explicitly so the operator doesn't see two
               identical numbers labelled differently. Rendered benign
               (green), unlike the NAS100 T-1 fallback (amber): this state
               is routine every morning and self-clears at the strike.
               See feedback_vs_close_vs_signal_distinct_baselines.md. -->
          <span v-if="gbpusdReturn != null" :class="gbpusdReturn >= 0 ? 'pill-positive' : 'pill-negative'">
            {{ gbpusdReturn >= 0 ? '▲' : '▼' }} {{ (gbpusdReturn * 100).toFixed(2) }}%
            <span class="return-label"
                  :class="{ 'return-label-benign': gbpusdRefDegraded }"
                  :title="gbpusdRefDegraded ? `Today’s ${strike} UK FX not yet captured — vs signal baseline falls back to the previous fix (same as vs close)` : ''">
              signal
            </span>
          </span>
          <span v-if="gbpusdMarketReturn != null && !gbpusdRefDegraded" :class="gbpusdMarketReturn >= 0 ? 'pill-positive' : 'pill-negative'">
            {{ gbpusdMarketReturn >= 0 ? '▲' : '▼' }} {{ (gbpusdMarketReturn * 100).toFixed(2) }}%
            <span class="return-label">vs close</span>
          </span>
          <span v-if="gbpusdRefDegraded" class="pill-benign" :title="`Signal and vs close baselines are identical — today's ${strike} UK FX rate not yet captured, ref falls back to the previous fix. Clears automatically at the strike.`">
            = vs close (pre-{{ strike }})
          </span>
        </div>
      </span>
      <span v-if="fxWindPill" class="ctx-pill" :class="fxWindPill.cls" :title="fxWindPill.title">
        FX {{ fxWindPill.label }}
      </span>
    </div>
  </CollapsibleCard>
</template>

<style scoped>
.context-pills {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.ctx-pill {
  padding: 3px 8px;
  background: #21262d;
  border-radius: 10px;
  font-size: 0.78rem;
  color: #8b949e;
}
.pill-value { color: #e6edf3; font-weight: 600; font-family: 'JetBrains Mono', monospace; }
.pill-positive { color: #3fb950; border: 1px solid #238636; }
.pill-negative { color: #f85149; border: 1px solid #f85149; }
.pill-returns { display: flex; gap: 10px; font-size: 11px; margin-top: 2px; }
.return-label { font-size: 9px; color: #666; margin-left: 2px; }
.return-label-warn { color: #d4a017; }
.return-label-benign { color: #3fb950; }
.pill-degraded { font-size: 9px; color: #d4a017; margin-left: 4px; }
/* Benign (green) variant of pill-degraded: routine pre-strike FX state,
   self-clears at the strike — deliberately not amber. */
.pill-benign { font-size: 9px; color: #3fb950; margin-left: 4px; }
</style>
